"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cva } from "class-variance-authority";
import { Icon } from "@/components/ui/Icon";
import type { SectionNavigationItem } from "@/types";
import { getTranslations, type Locale } from "@/lib/i18n/config";
import { useIsInView, useScrollSpy } from "@/lib/hooks";
import { ACTIVE_SECTION_TOLERANCE, SECTION_SCROLL_OFFSET } from "@/lib/constants/layout";
export interface SectionNavigationProps {
  items: SectionNavigationItem[];
  locale?: Locale;
  className?: string;
}
const itemVariants = cva("px-md py-sm rounded-4xl inline-flex items-center whitespace-nowrap font-weight-medium no-underline transition-colors duration-200 focus-outline-dark", {
  variants: {
    isActive: {
      true: "bg-purple text-white *:text-yellow",
      false: "text-purple hover:bg-purple/10"
    }
  },
  defaultVariants: {
    isActive: false
  }
});
export const SectionNavigation: React.FC<SectionNavigationProps> = t0 => {
  const $ = _c(14);
  const {
    items,
    locale: t1,
    className: t2
  } = t0;
  const locale = t1 === undefined ? "de" : t1;
  const className = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== locale) {
    t3 = getTranslations(locale);
    $[0] = locale;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t = t3.common;
  const prefersReducedMotion = useReducedMotion();
  let t4;
  if ($[2] !== items) {
    t4 = items.map(_temp);
    $[2] = items;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      readingLine: SECTION_SCROLL_OFFSET + ACTIVE_SECTION_TOLERANCE
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const activeTarget = useScrollSpy(t4, t5);
  const isFooterInView = useIsInView("footer");
  let t6;
  if ($[5] !== activeTarget || $[6] !== className || $[7] !== isFooterInView || $[8] !== items || $[9] !== prefersReducedMotion || $[10] !== t) {
    t6 = activeTarget !== null && !isFooterInView && <motion.nav aria-label={t.sectionNavigation} initial={{
      y: "100%"
    }} animate={{
      y: 0
    }} exit={{
      y: "100%"
    }} transition={{
      duration: prefersReducedMotion ? 0 : 0.2,
      ease: "easeInOut"
    }} className={`fixed w-screen bottom-0 z-45 hidden lg:block ${className}`}><ul className="flex flex-wrap items-center justify-center gap-xs bg-off-white/90 p-sm shadow-xl backdrop-blur-2xl">{items.map(item_0 => <li key={item_0.id}><a href={`#${item_0.target}`} aria-current={item_0.target === activeTarget ? true : undefined} className={itemVariants({
            isActive: item_0.target === activeTarget
          })}>{item_0.icon && <Icon name={item_0.icon} size={18} decorative={true} aria-hidden="true" className="mr-2 inline" />}{item_0.label}</a></li>)}</ul></motion.nav>;
    $[5] = activeTarget;
    $[6] = className;
    $[7] = isFooterInView;
    $[8] = items;
    $[9] = prefersReducedMotion;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <AnimatePresence>{t6}</AnimatePresence>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
function _temp(item) {
  return item.target;
}